import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Loader } from '../../components/ui';
import { cn } from '../../lib/utils';
import type { Topic } from '../../types/topic';
import { TOPIC_ICONS } from '../../constants/icons';

interface TopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic?: Topic;
  onSubmit: (name: string, description: string) => Promise<void>;
}

export function TopicModal({ isOpen, onClose, topic, onSubmit }: TopicModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form state
  useEffect(() => {
    if (topic) {
      setName(topic.name);
      setDescription(topic.description || '');
    } else {
      setName('');
      setDescription('');
    }
    setError(null);
  }, [topic, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(name.trim(), description.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save topic');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // This is definitely for editing an existing topic, so topic should exist
  // Add a defensive check just to be safe
  if (!topic) {
    return null;
  }
  
  const TypeIcon = TOPIC_ICONS[topic.topic_type]?.icon;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Edit Topic</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {TypeIcon && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TypeIcon size={20} className="text-blue-600" />
                </div>
                <div className="text-sm text-gray-600">{TOPIC_ICONS[topic.topic_type]?.label}</div>
              </div>
            )}

            <div>
              <label htmlFor="topic-name" className="block font-medium text-gray-700 mb-1">
                Topic Name
              </label>
              <input
                id="topic-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className={cn(
                  'w-full border rounded-lg px-3 py-2',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors duration-200'
                )}
                placeholder="Enter topic name"
                disabled={isSubmitting}
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="topic-description" className="block font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="topic-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className={cn(
                  'w-full border rounded-lg px-3 py-2',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'transition-colors duration-200',
                  'resize-none'
                )}
                placeholder="Enter topic description (optional)"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'px-4 py-2 bg-gray-100 text-gray-700 rounded-lg',
                  'hover:bg-gray-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={cn(
                  'px-4 py-2 bg-blue-500 text-white rounded-lg',
                  'hover:bg-blue-600 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center gap-2'
                )}
                disabled={!name.trim() || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size="small" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Changes</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
